'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell,  } from 'recharts';

type TabType = 'overview' | 'links' | 'performance' | 'payouts';

interface ReferralLink {
  id: string;
  name: string;
  code: string;
  url: string;
  clicks: number;
  conversions: number;
  revenue: number;
  commission: number;
  createdAt: string;
  status: 'active' | 'paused';
}

interface AffiliateStats {
  totalClicks: number;
  totalConversions: number;
  totalRevenue: number;
  totalCommission: number;
  pendingPayout: number;
  conversionRate: number;
}

const generateReferralCode = (name: string) => {
  const base = name.toUpperCase().replace(/\s+/g, '').slice(0, 6);
  const suffix = Math.random().toString(36).substring(2, 6).toUpperCase();
  return `${base}${suffix}`;
};

const initialLinks: ReferralLink[] = [
  {
    id: '1', name: 'Homepage Banner', code: 'HOME2024',
    url: 'https://travelgate7783.builtwithrocket.new/homepage?ref=HOME2024',
    clicks: 4820, conversions: 187, revenue: 42600, commission: 2130,
    createdAt: '2026-01-15', status: 'active',
  },
  {
    id: '2', name: 'Flight Search Widget', code: 'FLIGHT24',
    url: 'https://travelgate7783.builtwithrocket.new/flight-search?ref=FLIGHT24',
    clicks: 3210, conversions: 142, revenue: 32400, commission: 1620,
    createdAt: '2026-01-22', status: 'active',
  },
  {
    id: '3', name: 'Email Newsletter', code: 'EMAIL24',
    url: 'https://travelgate7783.builtwithrocket.new/booking?ref=EMAIL24',
    clicks: 1890, conversions: 78, revenue: 17800, commission: 890,
    createdAt: '2026-02-01', status: 'active',
  },
  {
    id: '4', name: 'Social Media Post', code: 'SOCIAL24',
    url: 'https://travelgate7783.builtwithrocket.new/holidays?ref=SOCIAL24',
    clicks: 920, conversions: 31, revenue: 7100, commission: 355,
    createdAt: '2026-02-10', status: 'paused',
  },
];

const performanceData = [
  { month: 'Oct', clicks: 3200, conversions: 124, commission: 2800 },
  { month: 'Nov', clicks: 4100, conversions: 158, commission: 3560 },
  { month: 'Dec', clicks: 6800, conversions: 264, commission: 5940 },
  { month: 'Jan', clicks: 5400, conversions: 210, commission: 4725 },
  { month: 'Feb', clicks: 4820, conversions: 187, commission: 4208 },
  { month: 'Mar', clicks: 5200, conversions: 202, commission: 4545 },
];

const topAffiliatesData = [
  { name: 'TravelBlog UK', code: 'TBUK24', clicks: 12400, conversions: 482, commission: 10845, tier: 'Gold' },
  { name: 'FlightDeals.co', code: 'FDCO24', clicks: 9800, conversions: 381, commission: 8573, tier: 'Gold' },
  { name: 'Budget Traveller', code: 'BUDG24', clicks: 7200, conversions: 280, commission: 6300, tier: 'Silver' },
  { name: 'UK Expats Forum', code: 'UKEX24', clicks: 5400, conversions: 210, commission: 4725, tier: 'Silver' },
  { name: 'Wanderlust Mag', code: 'WAND24', clicks: 3200, conversions: 124, commission: 2790, tier: 'Bronze' },
];

const commissionByTypeData = [
  { name: 'Flights', value: 62, color: '#0066CC' },
  { name: 'Hotels', value: 22, color: '#10B981' },
  { name: 'Holidays', value: 12, color: '#FF6B35' },
  { name: 'Onward Tickets', value: 4, color: '#8B5CF6' },
];

const payoutHistory = [
  { id: 'PAY-001', period: 'Feb 2026', amount: 4208, status: 'paid', date: '2026-03-01' },
  { id: 'PAY-002', period: 'Jan 2026', amount: 4725, status: 'paid', date: '2026-02-01' },
  { id: 'PAY-003', period: 'Dec 2025', amount: 5940, status: 'paid', date: '2026-01-01' },
  { id: 'PAY-004', period: 'Mar 2026', amount: 4545, status: 'pending', date: '2026-04-01' },
];

const tierColors: Record<string, string> = {
  Gold: 'bg-yellow-100 text-yellow-700',
  Silver: 'bg-gray-100 text-gray-600',
  Bronze: 'bg-orange-100 text-orange-700',
};

const AffiliateReferralInteractive = () => {
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [links, setLinks] = useState<ReferralLink[]>(initialLinks);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newLinkName, setNewLinkName] = useState('');
  const [newLinkTarget, setNewLinkTarget] = useState('/homepage');
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => { setIsHydrated(true); }, []);

  const stats: AffiliateStats = {
    totalClicks: links.reduce((s, l) => s + l.clicks, 0),
    totalConversions: links.reduce((s, l) => s + l.conversions, 0),
    totalRevenue: links.reduce((s, l) => s + l.revenue, 0),
    totalCommission: links.reduce((s, l) => s + l.commission, 0),
    pendingPayout: 4545,
    conversionRate: 0,
  };
  stats.conversionRate = parseFloat(((stats.totalConversions / stats.totalClicks) * 100).toFixed(2));

  const handleCopy = (url: string, id: string) => {
    navigator.clipboard.writeText(url).catch(() => {});
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleCreateLink = () => {
    if (!newLinkName.trim()) return;
    const code = generateReferralCode(newLinkName);
    const newLink: ReferralLink = {
      id: Date.now().toString(),
      name: newLinkName,
      code,
      url: `https://travelgate7783.builtwithrocket.new${newLinkTarget}?ref=${code}`,
      clicks: 0, conversions: 0, revenue: 0, commission: 0,
      createdAt: new Date().toISOString().split('T')[0],
      status: 'active',
    };
    setLinks((prev) => [newLink, ...prev]);
    setNewLinkName('');
    setShowCreateModal(false);
  };

  const toggleStatus = (id: string) => {
    setLinks((prev) => prev.map((l) => l.id === id ? { ...l, status: l.status === 'active' ? 'paused' : 'active' } : l));
  };

  const tabs = [
    { id: 'overview' as TabType, label: 'Overview', icon: 'ChartBarIcon' },
    { id: 'links' as TabType, label: 'Referral Links', icon: 'LinkIcon' },
    { id: 'performance' as TabType, label: 'Performance', icon: 'ArrowTrendingUpIcon' },
    { id: 'payouts' as TabType, label: 'Payouts', icon: 'BanknotesIcon' },
  ];

  if (!isHydrated) {
    return (
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="animate-pulse space-y-8">
          <div className="h-12 bg-gray-200 rounded-lg w-1/3" />
          <div className="h-64 bg-gray-200 rounded-lg" />
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-4xl md:text-5xl font-black text-foreground mb-2">Affiliate Referral Tracking</h1>
          <p className="text-lg text-muted-foreground">Manage referral links, track commissions &amp; monitor affiliate performance</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-primary to-secondary text-white font-bold rounded-xl hover:shadow-lg transition-all duration-300 hover:scale-105"
        >
          <Icon name="PlusIcon" size={20} />
          Create Referral Link
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
        {[
          { label: 'Total Clicks', value: stats.totalClicks.toLocaleString(), icon: 'CursorArrowRaysIcon', bg: 'bg-blue-50', text: 'text-blue-600' },
          { label: 'Conversions', value: stats.totalConversions.toLocaleString(), icon: 'CheckCircleIcon', bg: 'bg-green-50', text: 'text-green-600' },
          { label: 'Conv. Rate', value: `${stats.conversionRate}%`, icon: 'ArrowTrendingUpIcon', bg: 'bg-purple-50', text: 'text-purple-600' },
          { label: 'Revenue Generated', value: `£${(stats.totalRevenue / 1000).toFixed(1)}k`, icon: 'CurrencyPoundIcon', bg: 'bg-teal-50', text: 'text-teal-600' },
          { label: 'Total Commission', value: `£${(stats.totalCommission / 1000).toFixed(1)}k`, icon: 'BanknotesIcon', bg: 'bg-orange-50', text: 'text-orange-600' },
          { label: 'Pending Payout', value: `£${stats.pendingPayout.toLocaleString()}`, icon: 'ClockIcon', bg: 'bg-yellow-50', text: 'text-yellow-600' },
        ].map((card, i) => (
          <div key={i} className="bg-white rounded-xl p-4 border border-border hover:shadow-md transition-all">
            <div className={`w-10 h-10 ${card.bg} rounded-lg flex items-center justify-center mb-3`}>
              <Icon name={card.icon as any} size={20} className={card.text} />
            </div>
            <div className="text-xl font-black text-foreground">{card.value}</div>
            <div className="text-xs text-muted-foreground mt-0.5">{card.label}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-brand border border-border overflow-hidden mb-8">
        <div className="flex flex-wrap border-b border-border">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-5 py-4 font-bold transition-all duration-200 whitespace-nowrap ${
                activeTab === tab.id
                  ? 'text-primary border-b-2 border-primary bg-primary/5' :'text-muted-foreground hover:text-foreground hover:bg-surface'
              }`}
            >
              <Icon name={tab.icon as any} size={18} />
              {tab.label}
            </button>
          ))}
        </div>

        <div className="p-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div className="bg-white rounded-xl border border-border p-5">
                <h3 className="text-lg font-heading font-black text-foreground mb-4">Clicks &amp; Conversions Trend</h3>
                <ResponsiveContainer width="100%" height={260}>
                  <AreaChart data={performanceData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                    <defs>
                      <linearGradient id="clickGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#0066CC" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#0066CC" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="convGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10B981" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Legend />
                    <Area type="monotone" dataKey="clicks" stroke="#0066CC" fill="url(#clickGrad)" strokeWidth={2} name="Clicks" />
                    <Area type="monotone" dataKey="conversions" stroke="#10B981" fill="url(#convGrad)" strokeWidth={2} name="Conversions" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white rounded-xl border border-border p-5">
                  <h3 className="text-lg font-heading font-black text-foreground mb-4">Commission by Product Type</h3>
                  <div className="flex items-center gap-4">
                    <ResponsiveContainer width="50%" height={180}>
                      <PieChart>
                        <Pie data={commissionByTypeData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={3} dataKey="value">
                          {commissionByTypeData.map((d, i) => <Cell key={i} fill={d.color} />)}
                        </Pie>
                        <Tooltip formatter={(v) => [`${v}%`, 'Share']} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="flex-1 space-y-2">
                      {commissionByTypeData.map((d, i) => (
                        <div key={i} className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: d.color }} />
                            <span className="text-xs text-muted-foreground">{d.name}</span>
                          </div>
                          <span className="text-xs font-black text-foreground">{d.value}%</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-xl border border-border p-5">
                  <h3 className="text-lg font-heading font-black text-foreground mb-4">Monthly Commission Earned</h3>
                  <ResponsiveContainer width="100%" height={180}>
                    <BarChart data={performanceData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `£${(v / 1000).toFixed(1)}k`} />
                      <Tooltip formatter={(v: number) => [`£${v.toLocaleString()}`, 'Commission']} />
                      <Bar dataKey="commission" fill="#FF6B35" radius={[4, 4, 0, 0]} name="Commission" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          )}

          {/* Referral Links Tab */}
          {activeTab === 'links' && (
            <div className="space-y-4">
              {links.map((link) => (
                <div key={link.id} className="border border-border rounded-xl p-5 hover:shadow-md transition-all">
                  <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-4">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-bold text-foreground">{link.name}</h3>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${
                          link.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                        }`}>{link.status}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-mono bg-surface px-2 py-1 rounded text-muted-foreground">{link.code}</span>
                        <span className="text-xs text-muted-foreground">Created {link.createdAt}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleCopy(link.url, link.id)}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-primary/10 text-primary rounded-lg text-xs font-bold hover:bg-primary/20 transition-colors"
                      >
                        <Icon name={copiedId === link.id ? 'CheckIcon' : 'ClipboardDocumentIcon'} size={14} />
                        {copiedId === link.id ? 'Copied!' : 'Copy Link'}
                      </button>
                      <button
                        onClick={() => toggleStatus(link.id)}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-surface border border-border rounded-lg text-xs font-bold hover:bg-muted transition-colors"
                      >
                        <Icon name={link.status === 'active' ? 'PauseIcon' : 'PlayIcon'} size={14} />
                        {link.status === 'active' ? 'Pause' : 'Activate'}
                      </button>
                    </div>
                  </div>

                  <div className="text-xs text-muted-foreground font-mono bg-surface rounded-lg px-3 py-2 mb-4 truncate">
                    {link.url}
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {[
                      { label: 'Clicks', value: link.clicks.toLocaleString(), icon: 'CursorArrowRaysIcon', color: 'text-blue-600' },
                      { label: 'Conversions', value: link.conversions.toLocaleString(), icon: 'CheckCircleIcon', color: 'text-green-600' },
                      { label: 'Revenue', value: `£${link.revenue.toLocaleString()}`, icon: 'CurrencyPoundIcon', color: 'text-teal-600' },
                      { label: 'Commission', value: `£${link.commission.toLocaleString()}`, icon: 'BanknotesIcon', color: 'text-orange-600' },
                    ].map((m, i) => (
                      <div key={i} className="bg-surface rounded-lg p-3">
                        <div className="flex items-center gap-1.5 mb-1">
                          <Icon name={m.icon as any} size={14} className={m.color} />
                          <span className="text-xs text-muted-foreground">{m.label}</span>
                        </div>
                        <div className={`text-base font-black ${m.color}`}>{m.value}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Performance Tab */}
          {activeTab === 'performance' && (
            <div className="space-y-6">
              <div className="bg-white rounded-xl border border-border overflow-hidden">
                <div className="p-5 border-b border-border">
                  <h3 className="text-lg font-heading font-black text-foreground">Top Affiliate Partners</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-surface">
                      <tr>
                        {['Affiliate', 'Code', 'Clicks', 'Conversions', 'Commission', 'Tier'].map((h) => (
                          <th key={h} className="px-4 py-3 text-left text-xs font-bold text-muted-foreground uppercase tracking-wide">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {topAffiliatesData.map((aff, i) => (
                        <tr key={i} className="border-t border-border hover:bg-surface transition-colors">
                          <td className="px-4 py-3 font-bold text-foreground text-sm">{aff.name}</td>
                          <td className="px-4 py-3"><span className="text-xs font-mono bg-surface px-2 py-1 rounded">{aff.code}</span></td>
                          <td className="px-4 py-3 text-sm text-muted-foreground">{aff.clicks.toLocaleString()}</td>
                          <td className="px-4 py-3 text-sm font-bold text-green-600">{aff.conversions.toLocaleString()}</td>
                          <td className="px-4 py-3 text-sm font-black text-primary">£{aff.commission.toLocaleString()}</td>
                          <td className="px-4 py-3">
                            <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${tierColors[aff.tier]}`}>{aff.tier}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="bg-white rounded-xl border border-border p-5">
                <h3 className="text-lg font-heading font-black text-foreground mb-4">Commission Structure</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {[
                    { tier: 'Bronze', range: '0–£2,500/mo', rate: '4.5%', perks: 'Basic tracking, monthly payouts', color: 'border-orange-200 bg-orange-50' },
                    { tier: 'Silver', range: '£2,500–£7,500/mo', rate: '5.5%', perks: 'Priority support, bi-weekly payouts', color: 'border-gray-200 bg-gray-50' },
                    { tier: 'Gold', range: '£7,500+/mo', rate: '7.0%', perks: 'Dedicated manager, weekly payouts, bonuses', color: 'border-yellow-200 bg-yellow-50' },
                  ].map((t, i) => (
                    <div key={i} className={`rounded-xl p-5 border-2 ${t.color}`}>
                      <div className="text-lg font-black text-foreground mb-1">{t.tier} Tier</div>
                      <div className="text-2xl font-black text-primary mb-1">{t.rate}</div>
                      <div className="text-xs text-muted-foreground mb-3">{t.range}</div>
                      <div className="text-xs text-foreground">{t.perks}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Payouts Tab */}
          {activeTab === 'payouts' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {[
                  { label: 'Pending Payout', value: '£4,545', icon: 'ClockIcon', bg: 'bg-yellow-50', text: 'text-yellow-600', sub: 'Due Apr 1, 2026' },
                  { label: 'Total Paid Out', value: '£14,873', icon: 'CheckCircleIcon', bg: 'bg-green-50', text: 'text-green-600', sub: 'All time' },
                  { label: 'Commission Rate', value: '5.5%', icon: 'PercentBadgeIcon', bg: 'bg-blue-50', text: 'text-blue-600', sub: 'Silver Tier' },
                ].map((card, i) => (
                  <div key={i} className="bg-white rounded-xl p-5 border border-border">
                    <div className={`w-12 h-12 ${card.bg} rounded-xl flex items-center justify-center mb-3`}>
                      <Icon name={card.icon as any} size={24} className={card.text} />
                    </div>
                    <div className="text-2xl font-black text-foreground">{card.value}</div>
                    <div className="text-sm text-muted-foreground mt-0.5">{card.label}</div>
                    <div className={`text-xs font-bold ${card.text} mt-1`}>{card.sub}</div>
                  </div>
                ))}
              </div>

              <div className="bg-white rounded-xl border border-border overflow-hidden">
                <div className="p-5 border-b border-border">
                  <h3 className="text-lg font-heading font-black text-foreground">Payout History</h3>
                </div>
                <div className="divide-y divide-border">
                  {payoutHistory.map((p) => (
                    <div key={p.id} className="flex items-center justify-between px-5 py-4 hover:bg-surface transition-colors">
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                          p.status === 'paid' ? 'bg-green-100' : 'bg-yellow-100'
                        }`}>
                          <Icon name={p.status === 'paid' ? 'CheckCircleIcon' : 'ClockIcon'} size={20}
                            className={p.status === 'paid' ? 'text-green-600' : 'text-yellow-600'} />
                        </div>
                        <div>
                          <div className="font-bold text-foreground text-sm">{p.period}</div>
                          <div className="text-xs text-muted-foreground">{p.id} · {p.date}</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-black text-foreground">£{p.amount.toLocaleString()}</div>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${
                          p.status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                        }`}>{p.status}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Create Link Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-xl font-black text-foreground">Create Referral Link</h2>
              <button onClick={() => setShowCreateModal(false)} className="p-2 hover:bg-surface rounded-lg transition-colors">
                <Icon name="XMarkIcon" size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-bold text-foreground mb-2">Link Name</label>
                <input
                  type="text"
                  value={newLinkName}
                  onChange={(e) => setNewLinkName(e.target.value)}
                  placeholder="e.g. Instagram Bio Link"
                  className="w-full px-4 py-3 border border-border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-foreground mb-2">Target Page</label>
                <select
                  value={newLinkTarget}
                  onChange={(e) => setNewLinkTarget(e.target.value)}
                  className="w-full px-4 py-3 border border-border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="/homepage">Homepage</option>
                  <option value="/flight-search">Flight Search</option>
                  <option value="/hotels">Hotels</option>
                  <option value="/holidays">Holidays</option>
                  <option value="/booking">Booking</option>
                  <option value="/onward-ticket">Onward Ticket</option>
                </select>
              </div>
              {newLinkName && (
                <div className="bg-surface rounded-xl p-3">
                  <div className="text-xs text-muted-foreground mb-1">Preview URL</div>
                  <div className="text-xs font-mono text-foreground break-all">
                    https://travelgate7783.builtwithrocket.new{newLinkTarget}?ref={generateReferralCode(newLinkName)}
                  </div>
                </div>
              )}
            </div>

            <div className="flex gap-3 mt-6">
              <button onClick={() => setShowCreateModal(false)} className="flex-1 py-3 border border-border rounded-xl font-bold text-foreground hover:bg-surface transition-colors">
                Cancel
              </button>
              <button
                onClick={handleCreateLink}
                disabled={!newLinkName.trim()}
                className="flex-1 py-3 bg-primary text-white font-bold rounded-xl hover:bg-secondary transition-colors disabled:opacity-40"
              >
                Create Link
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AffiliateReferralInteractive;
